import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "tint";
type Size = "sm" | "md" | "lg" | "xl" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

const variants: Record<Variant, string> = {
  // Hero gradient CTA — same as `AppPrimaryButtonStyle` on iOS.
  primary: "hero-gradient text-on-hero shadow-[0_6px_14px_-2px_color-mix(in_srgb,var(--accent)_35%,transparent)] hover:brightness-105 active:scale-[0.98]",
  secondary: "bg-accent/12 text-accent border border-border hover:bg-accent/18 active:scale-[0.97]",
  tint: "bg-surface-elevated text-text border border-border hover:bg-surface active:scale-[0.97]",
  ghost: "text-text-secondary hover:bg-text/6 hover:text-text active:scale-[0.97]",
  danger: "bg-danger/12 text-danger hover:bg-danger/20 active:scale-[0.97]",
};
const sizes: Record<Size, string> = {
  sm: "min-h-9 px-3 py-1.5 text-sm gap-1.5",
  md: "min-h-11 px-5 py-2 text-[15px] gap-2",
  lg: "min-h-12 px-6 py-2 text-base gap-2 w-full",
  /** Same height as `lg`, natural width — hero / marketing calls to action. */
  xl: "min-h-12 px-6 py-2 text-base gap-2",
  icon: "h-10 w-10 shrink-0 p-0",
};

/** The button look as a class string — for `<Link>`s that should read as buttons (no button-inside-link nesting). */
export function buttonClasses(variant: Variant = "primary", size: Size = "md", className?: string): string {
  return cn(
    "inline-flex max-w-full select-none items-center justify-center whitespace-normal break-words text-center rounded-control font-semibold transition-[background,transform,filter] duration-150 disabled:opacity-50 disabled:pointer-events-none [&>svg]:shrink-0",
    variants[variant],
    sizes[size],
    className,
  );
}

export function Button({ variant = "primary", size = "md", loading, icon, className, children, disabled, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      // Icon-only buttons: the accessible name is also the hover tooltip.
      title={rest.title ?? (size === "icon" ? rest["aria-label"] : undefined)}
      className={buttonClasses(variant, size, className)}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}
